<script setup>
import { onMounted, ref, watch } from "vue";
import DatePicker from "@/components/frontend/ui/DatePicker.vue";
import ProductCombinationSelector from "@/components/frontend/booking/ProductCombinationSelector.vue";
import { useNotification } from "@kyvg/vue3-notification";
import SlotSelector from "@/components/frontend/booking/store/SlotSelector.vue";
import useBookingStoreSlotAvailability from "@/composables/useBookingStoreSlotAvailability.ts";
import dayjs from "dayjs";

const { notify }  = useNotification()

const { product, store } = defineProps({
  product: {
    type: Number,
    required: true
  },
  store: {
    type: Object,
    required: true
  },
});

const emit = defineEmits([
  'submit-booking',
  'combination-change',
]);

const date = ref(null);

const productCombinations = ref([]);
const fetchingVariants = ref(false);
const fetchVariants = async () => {
  fetchingVariants.value = true;
  const searchParams = new URLSearchParams('lang=' + window.Laravel.locale + '&filter[store_id]=' + store.id);
  const url = window.Laravel.apiBaseUrl + '/products/' + product.id + '/combinations?' + searchParams.toString();
  const response = await axios.get(url)
  productCombinations.value = response.data.data;

  fetchingVariants.value = false;
};

const combination = ref(null);
const onCombinationChange = async (c) => {
    combination.value = c;
    await fetchCalendar();
};

const year = ref(new Date().getFullYear());
const month = ref(new Date().getMonth() + 1);
const day = ref(null);
const allowedDates = ref([]);

const {
  isLoading: datePickerIsLoading,
  getAllowedDates,
  getAvailableSlots,
} = useBookingStoreSlotAvailability(
  window.Laravel.apiBaseUrl,
  window.Laravel.locale,
)

const fetchCalendar = async (count = 0) => {
  if (!combination.value) {
    return;
  }

  try {
    allowedDates.value =  await getAllowedDates(
      product.id,
      store.id,
      combination.value?.size,
      combination.value?.color,
      year.value,
      month.value,
    )
  } catch (error) {
    notify({
      group: 'errors',
      type: 'error',
      title: error.message,
    })

    allowedDates.value = [];
  }

  if (allowedDates.value.length === 0 && count < 1) {
    const nextMonth = dayjs(`${year.value}-${month.value}-${month.value}`, 'Y-m-d').add(1, 'month');
    year.value = nextMonth.year();
    month.value = nextMonth.month() + 1;
    await fetchCalendar(count + 1);
    return;
  }

  // select first allowedDates if current date is not allowed
  if (date.value && !allowedDates.value.find(d => dayjs(d).isSame(dayjs(date.value), 'day'))) {
    date.value = allowedDates.value[0] || null;
  }
};

const handleMonthYear = ({ month: m, year: y }) => {
  month.value = m + 1;
  year.value = y;
  fetchCalendar();
};

const slots = ref([]);
const slotSelected = ref(null);

const fetchSlots = async () => {
  slotSelected.value = null;
  if (!combination.value) {
    return;
  }
  if (!date.value) {
    return;
  }

  try {
    slots.value = await getAvailableSlots(
      product.id,
      store.id,
      combination.value?.size,
      combination.value?.color,
      year.value,
      month.value,
      day.value,
    )
  } catch (error) {
    slots.value = [];
    notify({
      group: "errors",
      type: "error",
      title: error.message
    });
  }
};

watch(date, async (newDate, oldDate) => {
  slotSelected.value = null;
  if (!newDate) {
    day.value = null;
    slots.value = [];
    return;
  }
  day.value = new Date(newDate).getDate()
  fetchSlots();
});

const onSlotChange = (s) => {
  slotSelected.value = s;
};


const onClickConfirmBooking = () => {
  emit('submitBooking', {
    store: store.id,
    start_at: slotSelected.value.start_at,
    end_at: slotSelected.value.end_at,
    equipment: slotSelected.value.availableEquipments[0],
  });
}


onMounted(async () => {
    await fetchVariants();
    if (productCombinations.value.length === 1) {
      onCombinationChange(productCombinations.value[0]);
    }
});

const datePickerMinDate = ref(new Date());

</script>

<template>
  <div class="row align-items-start">
    <div class="col-md-5 col-lg-3">
      <ProductCombinationSelector
        :product-combinations="productCombinations"
        :selectedCombination="combination"
        @combination-change="onCombinationChange"
      ></ProductCombinationSelector>
    </div>

    <div class="col-md-7 col-lg-5">
      <div class="form-group">
        <label for="bookingFormStart" class="font-weight-bold text-dark">{{ $t('Date:') }}</label>
        <DatePicker
          inline
          v-model="date"
          :min-date='datePickerMinDate'
          :loading="datePickerIsLoading"
          :allowed-dates="allowedDates"
          @update-month-year="handleMonthYear"
          class="date-picker"
        ></DatePicker>
      </div>
    </div>

    <div class="col-lg-4">
      <div class="form-group">
        <label for="bookingFormSize" class="font-weight-bold text-dark">{{ $t('Slot:') }}</label>
        <SlotSelector
          :slots="slots"
          :disabled="!date"
          :selectedSlot="slotSelected"
          @slot-change="onSlotChange"
        ></SlotSelector>
      </div>
    </div>
  </div>

  <div class="form-group mt-5">
    <button class="btn btn-primary btn-block btn-lg d-flex align-items-center justify-content-center"
            v-on:click.prevent="onClickConfirmBooking()"
            :disabled="!slotSelected">
          <span>
              {{ $t('Book now') }}
          </span>
    </button>
  </div>
</template>

<style scoped>

</style>